import { AnalyticsContext } from '@carvana/eventable-clickstream';
import { DispatcherEvent } from '@carvana/merch-native/index';
import React, { useCallback, useContext } from 'react';
import { useIsLoggedIn } from '~/context/common/UserContext';
import ModalContext from '~/context/home/ModalContext';
import RecentSearchesContext from '~/context/home/RecentSearchesContext';
import SavedSearchesContext from '~/context/home/SavedSearchesContext';
import { RecentSearch } from '~/contracts/home';
import { useOpenAccountModal } from '~/hooks/common/useAccountModal';
import { deleteSavedSearch } from '~/services/home';

export enum ToggleSavedSearchSource {
  HomeFeed = 'home-feed',
  HomeExp = 'home-exp'
}

type OwnProps = {
  search: RecentSearch;
  source: ToggleSavedSearchSource;
};

const updateRecentOnRemove = (
  recentReaches: RecentSearch[],
  targetCvnaId: string
) =>
  recentReaches.map(recentSearch => {
    if (recentSearch.cvnaId !== targetCvnaId) return recentSearch;

    return {
      ...recentSearch,
      savedSearch: undefined
    };
  });

export default function useToggleSavedSearches({ search, source }: OwnProps) {
  const { savedSearch, display, cvnaId } = search;
  const alertName = savedSearch?.name || display.name;
  const isHomeFeedSource = source === ToggleSavedSearchSource.HomeFeed;
  const { trackEvent } = useContext(AnalyticsContext);
  const { triggerFetchSavedSearches } = useContext(SavedSearchesContext);
  const {
    recentSearches: { data: recentSearches, setData: setRecentSearches }
  } = useContext(RecentSearchesContext);
  const { setCreateSavedSearchOpen } = useContext(ModalContext);
  const isLoggedIn = useIsLoggedIn();

  const successHandler = useCallback(() => {
    setCreateSavedSearchOpen(true, {
      alertName,
      cvnaid: cvnaId
    });
  }, [setCreateSavedSearchOpen, alertName, cvnaId]);

  const openAccountModal = useOpenAccountModal();

  const openModal = useCallback(() => {
    if (isLoggedIn) successHandler();

    openAccountModal({
      nativeOptions: {
        eventDispatch: DispatcherEvent.AddSavedSearch,
        eventPayload: {
          alertName,
          cvnaid: cvnaId
        },
        eventSource: 'home'
      },
      onSuccess: successHandler
    });
  }, [alertName, cvnaId, isLoggedIn, openAccountModal, successHandler]);

  return useCallback(
    (event: React.MouseEvent) => {
      event.preventDefault();
      event.stopPropagation();

      if (savedSearch?.savedSearchId) {
        deleteSavedSearch(savedSearch.savedSearchId).then(() => {
          triggerFetchSavedSearches();
          setRecentSearches(updateRecentOnRemove(recentSearches, cvnaId));
        });

        trackEvent('Clicked - Home feed card', {
          description: isHomeFeedSource
            ? 'Recent search heart cta'
            : 'Search Card',
          ...(isHomeFeedSource
            ? { status: 'removed', content: display }
            : { type: 'recent', item: 'heart' })
        });

        return;
      }

      openModal();

      trackEvent('Clicked - Home feed card', {
        description: isHomeFeedSource
          ? 'Recent search heart cta'
          : 'Search Card',
        ...(isHomeFeedSource
          ? { status: 'opened', content: display }
          : { type: 'recent', item: 'heart' })
      });
    },
    [
      cvnaId,
      display,
      isHomeFeedSource,
      openModal,
      recentSearches,
      savedSearch?.savedSearchId,
      setRecentSearches,
      trackEvent,
      triggerFetchSavedSearches
    ]
  );
}
